import { useEffect, useRef } from 'react';
import type { SparkActivityStageForm } from '../../types/spark-activity';
import { useDomains } from '../../hooks/useDomains';
import { useDomainSkills } from '../../hooks/useDomainSkills';
import StageColumn from './StageColumn';

interface StageLadderProps {
  stages: SparkActivityStageForm[];
  onStageChange: (index: number, field: keyof SparkActivityStageForm, value: unknown) => void;
  errors: Record<string, string>;
  focusedCol: number | null;
  onFocusedColChange: (index: number | null) => void;
}

export default function StageLadder({ stages, onStageChange, errors, focusedCol, onFocusedColChange }: StageLadderProps) {
  const { data: domains = [] } = useDomains();
  const { data: skills = [] } = useDomainSkills();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onFocusedColChange(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onFocusedColChange]);

  const gridCols =
    focusedCol === null
      ? '1fr 1fr 1fr 1fr 1fr'
      : Array.from({ length: 5 }, (_, i) => (i === focusedCol ? '1.8fr' : '0.7fr')).join(' ');

  return (
    <div className="mt-[26px]">
      <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-[10px]">
        The Activity by Communication Level (L1 to L5)
      </p>
      <div
        ref={containerRef}
        className="border-[1.5px] border-pine-line rounded-[12px] overflow-hidden bg-[#FBFDFC]"
      >
        <div className="overflow-x-auto">
          <div
            className="grid min-w-[800px] transition-[grid-template-columns] duration-200 ease-out"
            style={{ gridTemplateColumns: gridCols }}
          >
          {stages.map((stage, i) => (
            <StageColumn
              key={stage.communicationStageId}
              stage={stage}
              levelIndex={i}
              isFocused={focusedCol === i}
              onFocus={() => onFocusedColChange(i)}
              onChange={(field, value) => onStageChange(i, field, value)}
              errors={errors}
              domains={domains}
              skills={skills}
            />
          ))}
        </div>
        </div>
      </div>
    </div>
  );
}
